/**
 * engine/datas.ts — aritmética de mês (tipo `Ym`) e arredondamento.
 * Cópia literal de `fincast-viabilidade-v67.html`, linhas 1322-1326.
 *
 * Nenhuma destas funções usa relógio: `ymOf` NÃO cria `Date`, portanto é imune a fuso
 * horário. Manter assim (o motor roda no browser e no servidor com o mesmo resultado).
 * `addDays` cria `Date`, mas só em UTC — imune a fuso desde 29/07/2026 (ver a nota nela).
 */

import type { IsoDate, Ym } from './tipos';

/**
 * v67 linha 1322: `const teto=Math.ceil;`
 * Usado em `vendExig`/`finExig`. NÃO trocar por `Math.round` — mudaria o mínimo de
 * unidades exigidas.
 */
export const teto: (x: number) => number = Math.ceil;

/**
 * v67 linha 1323. `'2026-09-15'` → 2026*12 + 8.
 * Devolve `null` quando `d` é falsy (string vazia inclusive).
 */
export function ymOf(d: IsoDate | '' | null | undefined): Ym | null {
  if (!d) return null;
  const [y, m] = d.split('-').map(Number);
  return y * 12 + (m - 1);
}

/**
 * v67 linha 1324 — formato 'mmm.aa' (ex.: 'jul.26'), meses PT-BR hardcoded.
 * É APRESENTAÇÃO: só está aqui porque `calcViab` monta frases. Ver `formato.ts`.
 */
export function ymLabel(ym: Ym): string {
  const y = Math.floor(ym / 12), m = ym % 12;
  return ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'][m] + '.' + String(y).slice(2);
}

/** v67 linha 1325. `m0` é mês 0-based (janeiro = 0). */
export function ymFromParts(y: number, m0: number): Ym {
  return y * 12 + m0;
}

/**
 * v67 linha 1326 — soma de dias de CALENDÁRIO. `addDays('2026-07-01', 30)`→`'2026-07-31'`.
 *
 * ⚠ CORRIGIDA em 29/07/2026 (pré-requisito da Parte B do IGH). O protótipo fazia
 * `new Date(d+'T00:00:00')` (nasce em horário LOCAL) e devolvia `toISOString()` (UTC): a
 * LESTE de Greenwich a meia-noite local é o dia ANTERIOR em UTC, e o `slice(0,10)` cortava
 * um dia — `addDays(d, 0)` chegava a devolver o dia anterior e `addDays(d, 1)` virava NO-OP.
 * A OESTE (Brasil, UTC-3) e em UTC acertava, e é por isso que o defeito nunca apareceu.
 *
 * Agora a aritmética é toda em UTC, sem passar por horário local: o resultado é o MESMO em
 * qualquer fuso — condição para o motor rodar no navegador e no servidor com o mesmo
 * número. Nos fusos em que o produto roda hoje a saída é idêntica à de antes.
 *
 * Mantém o contrato de falhar alto: data inválida estoura `RangeError` ("Invalid time
 * value"), como no protótipo.
 */
export function addDays(d: IsoDate, n: number): IsoDate {
  const [y, m, dia] = String(d).split('-').map(Number);
  const dt = new Date(Date.UTC(y, (m as number) - 1, dia));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}
